/**
 * Public i18n API for the broadcast panel.
 *
 * - t(key, vars?): get a translated string for the active language.
 *   Missing in active lang → warn + fallback to English; missing in English too → warn + return key.
 * - applyTranslations(root): walk `root` and replace text of any element carrying
 *   `data-i18n`, `data-i18n-placeholder`, `data-i18n-title` attributes.
 *   Re-run this after `setLang()` to repaint the panel.
 * - subscribeLang: convenience re-export so callers don't need to reach into lang.ts.
 *
 * Variables inside translation strings use `{name}` syntax, e.g. "Hi {user}".
 */

import type { BroadcastI18nKey } from './keys';

import { en } from './en';
import { type BroadcastLang, getLang } from './lang';
import { zh } from './zh';

export { getLang, setLang, detectLang } from './lang';
export type { BroadcastLang };
export type { BroadcastI18nKey } from './keys';

const WARNED = new Set<string>();

function format(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name) => {
    const v = vars[name];
    return v === undefined ? m : String(v);
  });
}

/**
 * Get the translation of `key` in the active language.
 * - Active lang missing → fallback to English.
 * - English also missing → warn once per key, return key as last resort.
 */
export function t(key: BroadcastI18nKey, vars?: Record<string, string | number>): string {
  const active = getLang();
  const maps: Record<BroadcastLang, Record<BroadcastI18nKey, string>> = { zh, en };
  const activeMap = maps[active];
  const fallbackMap = maps.en;

  if (activeMap[key] !== undefined) {
    return format(activeMap[key], vars);
  }
  if (fallbackMap[key] !== undefined) {
    if (!WARNED.has(`${active}.${key}`)) {
      WARNED.add(`${active}.${key}`);
      // eslint-disable-next-line no-console
      console.warn(`[i18n] Missing ${active} translation for "${key}", using English fallback.`);
    }
    return format(fallbackMap[key], vars);
  }
  if (!WARNED.has(`*missing.${key}`)) {
    WARNED.add(`*missing.${key}`);
    // eslint-disable-next-line no-console
    console.warn(`[i18n] No translation for key "${key}".`);
  }
  return format(key, vars);
}

// ----- DOM repaint -----

const ATTRS = ['data-i18n', 'data-i18n-placeholder', 'data-i18n-title'] as const;

const VAR_ATTR = 'data-i18n-vars';

/**
 * Parse `data-i18n-vars` JSON safely; non-JSON / absent → undefined.
 */
function readVars(el: HTMLElement): Record<string, string | number> | undefined {
  const raw = el.getAttribute(VAR_ATTR);
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed as Record<string, string | number>;
  } catch {
    // ignore malformed JSON; t() will simply ignore missing vars
  }
  return undefined;
}

/**
 * Walks `root` (inclusive) once and applies translations to every element with an
 * i18n attribute. Cheap enough to call on every modal-open and every lang change.
 */
export function applyTranslations(root: HTMLElement): void {
  for (const attr of ATTRS) {
    const nodes = root.querySelectorAll<HTMLElement>(`[${attr}]`);
    nodes.forEach((el) => {
      const key = el.getAttribute(attr) as BroadcastI18nKey;
      if (!key) return;
      const value = t(key, readVars(el));
      if (attr === 'data-i18n') {
        el.textContent = value;
      } else if (attr === 'data-i18n-placeholder') {
        (el as HTMLInputElement | HTMLTextAreaElement).placeholder = value;
      } else if (attr === 'data-i18n-title') {
        el.title = value;
      }
    });
  }
}

/**
 * Re-exported so consumers can subscribe without importing from lang.ts directly.
 * (Kept here for grep convenience; identical to the one re-exported from './lang'.)
 */
export { subscribeLang } from './lang';
